/**
 * xScan web — useLocale hook for React components.
 *
 * Persists locale across navigations + remounts via localStorage.
 * Returns [locale, t, setLocale] tuple for ergonomic destructuring.
 */
import { useState, useEffect, useCallback } from 'react';
import { Locale, DEFAULT_LOCALE, t as translate, readStoredLocale, writeStoredLocale } from './dict';

export function useLocale(): {
  locale: Locale;
  t: (key: string) => string;
  setLocale: (next: Locale) => void;
  toggleLocale: () => void;
} {
  // Initialize from localStorage if available (lazy initializer avoids SSR mismatch)
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    return readStoredLocale();
  });

  // Sync if locale changes from another tab/window
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: StorageEvent) => {
      if (e.key === 'xscan_locale' && (e.newValue === 'es' || e.newValue === 'en')) {
        setLocaleState(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  const toggleLocale = useCallback(() => {
    setLocaleState((prev) => {
      const next = prev === 'es' ? 'en' : 'es';
      writeStoredLocale(next);
      return next;
    });
  }, []);

  const tBound = useCallback((key: string) => translate(key, locale), [locale]);

  return { locale, t: tBound, setLocale, toggleLocale };
}
